import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxFileManagerModule, DxTextBoxModule, DxLoadIndicatorModule, DxLoadPanelModule, DxSelectBoxModule, DxButtonModule, DxTooltipModule } from 'devextreme-angular';
import { DocumentsService } from 'src/app/services/documents.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AppGlobals } from 'src/app/app.globals';
import { FileViewerComponent } from 'src/app/components/file-viewer/file-viewer.component';
import { environment } from 'src/environments/environment';
import { FileViewerService } from 'src/app/services/file-viewer.service';
import { SandboxService } from 'src/app/services/sandbox.service';
import { FeatureFlags } from 'src/app/interfaces/enums/feature-flag';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GenericPopupComponent } from 'src/app/components/generic-popup/generic-popup.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';

import { SearchableDropdownDirective } from '../../../../../directives/searchable-dropdown.directive';
import { ApiFileService } from 'src/app/services/api-file.service';
@Component({
    selector: 'app-project-documents',
    imports: [CommonModule, DxFileManagerModule, DxTextBoxModule, DxLoadIndicatorModule, DxLoadPanelModule, DxSelectBoxModule, DxButtonModule, DxTooltipModule, GpdButtonComponent, GenericPopupComponent, SvgIconComponent, SearchableDropdownDirective],
    templateUrl: './project-documents.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./project-documents.component.css']
})
export class ProjectDocumentsComponent implements OnInit {
  private readonly apiFileService = inject(ApiFileService);


  @ViewChild('docView') docView;

  @ViewChild('sandboxView') sandboxView;

  @ViewChild(FileViewerComponent) fileViewer: FileViewerComponent;

  projectId: any;
  files = new Array();

  filesBackup = new Array();

  tempSearchFiles: any = new Array();
  searchTerm: string = '';

  preselectedFolder: string = null;

  filesLoaded: boolean = false;
  searchLoaded: boolean = false;

  sandboxFiles: any[] = [];
  sandboxFilesBackup: any[] = [];
  sandboxSearchTerm: string = '';
  sandboxFilesLoaded = false;
  sandboxRootFolderUrl: string = null;
  currentSandboxDirectory: any = null;
  currentSandboxPath: string = '';
  sandboxUploading = false;
  sandboxError: string = null;

  get mainStorageFolderUrl(): string | null {
    return this.files[0]?.linkingUrl ?? null;
  }

  openInSharePoint(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  newFolderPopupVisible = false;
  newFolderName = '';
  creatingFolder = false;

  get newFolderButtons() {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'secondary' as const,
        onClick: () => this.cancelCreateFolder()
      },
      {
        text: $localize`:@@create:Create`,
        type: 'primary' as const,
        disabled: !this.newFolderName?.trim() || this.creatingFolder,
        onClick: () => this.confirmCreateFolder()
      }
    ];
  }

  sandboxStatusLoaded = false;
  sandboxLinkConfigured = false;
  sandboxFolderLinked = false;
  sandboxFolderItemId: string = null;

  rootFolders: { name: string; itemId: string }[] = [];
  rootFoldersLoaded = false;
  selectedFolderItemId: string = null;
  linkingFolder = false;
  unlinkingFolder = false;
  disconnectPopupVisible = false;

  get disconnectButtons() {
    return [
      { text: $localize`:@@cancel:Cancel`, type: 'secondary' as const, onClick: () => { this.disconnectPopupVisible = false; } },
      { text: $localize`:@@disconnectSandboxFolder:Verknüpfung trennen`, type: 'danger' as const, disabled: this.unlinkingFolder, onClick: () => this.unlinkSandboxFolder() }
    ];
  }

  get isAdmin(): boolean {
    return AppGlobals.UserPermissions?.isAdministrator === true;
  }

  get canEditSandbox(): boolean {
    return AppGlobals.UserPermissions?.canEdit === true;
  }

  selectedTab = 0;
  tabs: { title: string }[] = [{ title: $localize`:@@mainStorage:Main Storage` }];


  private readonly folderIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#545454" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 19V7.54902C2 6.10516 2 5.38322 2.24332 4.81647C2.5467 4.10985 3.10985 3.5467 3.81647 3.24332C4.38322 3 5.09805 3 6.54902 3H7.04311C7.64819 3 8.22075 3.27394 8.60041 3.74509L10.4175 6M10.4175 6H16C17.4001 6 18.1002 6 18.635 6.27248C19.1054 6.51217 19.4878 6.89462 19.7275 7.36502C20 7.8998 20 8.59987 20 10V11M10.4175 6H7"/><path d="M3.15802 15.5144L3.45643 14.7717C4.19029 12.9449 4.55723 12.0316 5.3224 11.5158C6.08757 11 7.07557 11 9.05157 11H17.1119C19.8004 11 21.1446 11 21.7422 11.8787C22.3397 12.7575 21.8405 14.0002 20.842 16.4856L20.5436 17.2283C19.8097 19.0551 19.4428 19.9684 18.6776 20.4842C17.9124 21 16.9244 21 14.9484 21H6.88812C4.19961 21 2.85535 21 2.25782 20.1213C1.66029 19.2425 2.15953 17.9998 3.15802 15.5144Z"/></svg>`;

  private readonly fileIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#545454" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 19H12.5C9.67 19 8.26 19 7.38 18.12C6.5 17.24 6.5 15.83 6.5 13V8C6.5 5.17 6.5 3.76 7.38 2.88C8.26 2 9.67 2 12.5 2H13.84C14.66 2 15.07 2 15.44 2.15C15.8 2.3 16.09 2.59 16.67 3.17L19.33 5.83C19.91 6.41 20.2 6.7 20.35 7.06C20.5 7.43 20.5 7.84 20.5 8.66V13C20.5 15.83 20.5 17.24 19.62 18.12C18.74 19 17.33 19 14.5 19Z"/><path d="M15 2.5V3.5C15 5.39 15 6.33 15.59 6.91C16.17 7.5 17.11 7.5 19 7.5H20"/><path d="M6.5 5C4.84 5 3.5 6.34 3.5 8V16C3.5 18.83 3.5 20.24 4.38 21.12C5.26 22 6.67 22 9.5 22H14.5C16.16 22 17.5 20.66 17.5 19"/><path d="M10 11H14M10 15H17" stroke-width="1.5"/></svg>`;

  customizeThumbnail = (item: any): string => {
    return item.isDirectory ? '/assets/icons/folder.svg' : '/assets/icons/document.svg';
  };

  translateItems = {
    refresh: $localize`Refresh`,
    detailedView: $localize`Details View`,
    files: $localize`Files`,
    selectFolder: $localize`:@@selectSandboxFolder:Select folder`,
  }
  /*
  contextMenu: any = {
    items: [
      {
        text: 'Download',
        icon: 'download',
      }
    ],
    onItemClick: this.downloadFile.bind(this),
  };
*/

  constructor(
    private service: DocumentsService,
    private activatedRoute: ActivatedRoute,
    private appGlobals: AppGlobals,
    private router: Router,
    private fileViewerService: FileViewerService,
    private sandboxService: SandboxService,
  ) { }

  ngOnInit(): void {
    if (AppGlobals.UserCan(FeatureFlags.Sandbox)) {
      this.tabs = [
        { title: $localize`:@@mainStorage:Main Storage` },
        { title: $localize`:@@sandboxTab:Sandbox` },
      ];
    }

    if (environment.isTestEnvironment === true) {
      this.appGlobals.SetGlobalMenuIndex(2);
    } else {
      this.appGlobals.SetGlobalMenuIndex(2);
    }
    this.projectId = this.activatedRoute.snapshot.params.id;

    let params = {
      projectId: Number(this.projectId),
      parentFolders: null,
      folderName: null
    }

    this.service.getProjectSharepointDirectory({ RequestItem: params }, false).then((result) => {
      if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
      else {
        this.files.push(result.rootFolder);

        this.preselectedFolder = result.preSelectedFolder;

        this.filesBackup = new Array();
        this.filesBackup.push(result.rootFolder);

        this.filesLoaded = true;
        this.searchLoaded = true;
        return;
        setTimeout(() => {
          this.docView.instance.refresh();
          this.docView.instance.repaint();
        }, 1000)





      }
    },
      (error: any) => {
        AppGlobals.ShowErrorMessage(error);
      });

    if (AppGlobals.UserCan(FeatureFlags.Sandbox)) {
      this.loadSandboxStatus();
    }
  }


  loadSandboxStatus(): void {
    const projectIdNum = Number(this.projectId);
    if (!Number.isFinite(projectIdNum)) return;
    this.sandboxService.getSiteSandboxStatus({ ItemId: projectIdNum }).then((result) => {
      if (result?.hasError) {
        this.sandboxError = result.feedback;
        this.sandboxStatusLoaded = true;
        return;
      }
      this.sandboxLinkConfigured = result.sandboxLinkConfigured;
      this.sandboxFolderLinked = result.folderLinked;
      this.sandboxFolderItemId = result.folderItemId;
      this.sandboxStatusLoaded = true;

      if (!this.sandboxLinkConfigured) {
        this.sandboxError = $localize`:@@sandboxNoLinkConfigured:No sandbox link has been configured. Please contact your administrator.`;
      } else if (this.sandboxFolderLinked) {
        this.loadSandboxDirectory();
      } else {
        this.loadRootFolders();
      }
    });
  }

  loadSandboxDirectory(preservePath: boolean = false): void {
    const projectIdNum = Number(this.projectId);
    if (!Number.isFinite(projectIdNum)) return;
    if (!preservePath) this.sandboxFilesLoaded = false;
    const savedPath = preservePath ? this.currentSandboxPath : null;
    this.sandboxService.getSiteSandboxDirectory({ ItemId: projectIdNum }, !preservePath).then((result) => {
      if (result?.hasError) {
        this.sandboxError = result.feedback;
      } else if (result?.rootFolder) {
        const rf = result.rootFolder;
        this.sandboxRootFolderUrl = rf?.linkingUrl ?? null;
        this.sandboxFiles = rf?.items ?? [];
        this.sandboxFilesBackup = this.sandboxFiles;
        this.sandboxSearchTerm = '';
        this.sandboxFilesLoaded = true;
        if (preservePath) {
          // The file manager stays mounted; refresh its data in place and
          // restore the directory the user was working in.
          setTimeout(() => {
            const instance = this.sandboxView?.instance;
            if (!instance) return;
            const restore = () => { if (savedPath != null) instance.option('currentPath', savedPath); };
            const refreshResult = instance.refresh();
            if (refreshResult && typeof refreshResult.then === 'function') refreshResult.then(restore);
            else restore();
          });
        }
      }
    });
  }

  onSandboxDirectoryChanged(e: any): void {
    this.currentSandboxDirectory = e.directory;
    this.currentSandboxPath = e.directory?.path ?? '';
  }

  onSandboxFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const files = Array.from(input.files);
    this.uploadSandboxFiles(files).then(() => {
      input.value = '';
    });
  }

  onSandboxDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onSandboxDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (!files.length) return;
    this.uploadSandboxFiles(files);
  }

  private uploadSandboxFiles(files: File[]): Promise<void> {
    const parentItemId = this.currentSandboxDirectory?.dataItem?.itemId ?? this.sandboxFolderItemId;
    if (parentItemId == null) return Promise.resolve();
    this.sandboxUploading = true;
    return Promise.all(files.map(file => {
      const formData = new FormData();
      formData.append('File', file, file.name);
      formData.append('ParentItemId', parentItemId);
      formData.append('ProjectId', String(this.projectId));
      return this.sandboxService.uploadSandboxFile(formData);
    })).then(results => {
      this.sandboxUploading = false;
      const failed = results.find((r: any) => r?.hasError);
      if (failed) AppGlobals.ShowErrorMessage((failed as any).feedback ?? 'Upload failed');
      // Reload in place so the user keeps their current folder context.
      this.loadSandboxDirectory(true);
    }).catch(() => {
      this.sandboxUploading = false;
    });
  }

  triggerSandboxFileInput(): void {
    document.getElementById('sandbox-file-input')?.click();
  }

  openNewFolderDialog(): void {
    this.newFolderName = '';
    this.newFolderPopupVisible = true;
  }

  cancelCreateFolder(): void {
    this.newFolderPopupVisible = false;
  }

  confirmCreateFolder(): void {
    const folderName = (this.newFolderName ?? '').trim();
    const projectIdNum = Number(this.projectId);
    if (!folderName || this.creatingFolder || !Number.isFinite(projectIdNum)) return;
    const parentItem = this.currentSandboxPath ? this.findSandboxItemByPath(this.currentSandboxPath) : null;
    const parentItemId = parentItem?.itemId ?? this.sandboxFolderItemId;
    this.creatingFolder = true;
    this.sandboxService.createSandboxFolder({
      ProjectId: projectIdNum,
      ParentItemId: parentItemId,
      FolderName: folderName,
    }).then((result) => {
      this.creatingFolder = false;
      if (result?.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback ?? 'The folder could not be created.');
        return;
      }
      this.newFolderPopupVisible = false;
      // Reload in place so the user keeps their current folder context.
      this.loadSandboxDirectory(true);
    }).catch(() => {
      this.creatingFolder = false;
    });
  }

  onSandboxItemDeleting(e: any): void {
    const itemId = e.item?.dataItem?.itemId;
    if (!itemId) { e.cancel = true; return; }
    e.cancel = this.sandboxService.deleteSandboxItem({ DriveItemId: itemId })
      .then((result: any) => {
        if (result?.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback ?? 'Delete failed');
          return true;
        }
        return false;
      });
  }

  onSandboxItemRenaming(e: any): void {
    const itemId = e.item?.dataItem?.itemId;
    const newName = (e.newName ?? '').trim();
    if (!itemId || !newName) { e.cancel = true; return; }
    e.cancel = this.sandboxService.renameSandboxItem({ DriveItemId: itemId, NewName: newName })
      .then((result: any) => {
        if (result?.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback ?? 'Rename failed');
          return true;
        }
        return false;
      });
  }

  private findSandboxItemByPath(path: string): any {
    const segments = path.split('/').filter(s => s);
    let items: any[] = this.sandboxFiles;
    let found: any = null;
    for (const segment of segments) {
      found = items?.find((item: any) => item.name === segment) ?? null;
      if (!found) return null;
      items = found.items;
    }
    return found;
  }

  onSandboxItemMoving(e: any): void {
    const sourceItemId = e.item?.dataItem?.itemId;
    const destPath = (e.destinationDirectory?.path ?? '').trim();
    const destItem = destPath ? this.findSandboxItemByPath(destPath) : null;
    const destItemId = destItem?.itemId ?? this.sandboxFolderItemId;
    if (!sourceItemId || !destItemId) { e.cancel = true; return; }
    e.cancel = this.sandboxService.moveSandboxItem({ SourceItemId: sourceItemId, DestinationItemId: destItemId })
      .then((result: any) => {
        if (result?.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback ?? 'Move failed');
          return true;
        }
        this.loadSandboxDirectory(true);
        return false;
      });
  }

  openSandboxFile(e: any): void {
    const item = e?.file?.dataItem;
    if (!item || !item.itemId) return;
    this.fileViewerService.openOrDownloadFile('sandbox-document', item.itemId, item.name, item.serverRelativeUrl ?? '');
  }

  loadRootFolders(): void {
    this.rootFoldersLoaded = false;
    this.sandboxService.getSandboxRootFolders({}).then((result) => {
      if (result?.hasError) {
        this.sandboxError = result.feedback;
      } else {
        this.rootFolders = (result?.folders ?? []).map((f: any) => ({ name: f.name, itemId: f.itemId }));
        this.rootFoldersLoaded = true;
      }
    });
  }

  linkSandboxFolder(): void {
    const projectIdNum = Number(this.projectId);
    if (!this.selectedFolderItemId || !Number.isFinite(projectIdNum)) return;
    this.linkingFolder = true;
    this.sandboxService.linkSiteSandboxFolder({ ItemId: projectIdNum, FolderItemId: this.selectedFolderItemId }).then((result) => {
      this.linkingFolder = false;
      if (result?.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.sandboxFolderLinked = true;
        this.sandboxFolderItemId = this.selectedFolderItemId;
        this.selectedFolderItemId = null;
        this.loadSandboxDirectory();
      }
    });
  }

  unlinkSandboxFolder(): void {
    const projectIdNum = Number(this.projectId);
    if (!Number.isFinite(projectIdNum)) return;
    this.unlinkingFolder = true;
    this.sandboxService.unlinkSiteSandboxFolder({ ItemId: projectIdNum }).then((result) => {
      this.unlinkingFolder = false;
      if (result?.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
      } else {
        this.disconnectPopupVisible = false;
        this.sandboxFolderLinked = false;
        this.sandboxFolderItemId = null;
        this.sandboxFiles = [];
        this.sandboxFilesLoaded = false;
        this.loadRootFolders();
      }
    });
  }

  onToolbarChanged(e) {
    //cancel event
  }

  openFile(e) {

    this.fileViewerService.openOrDownloadFile(
      'project-document',
      e.file.dataItem.id,
      e.file.dataItem.name,
      e.file.dataItem.serverRelativeUrl
    );


    return;

    if (this.fileViewer == null) return;

    this.fileViewer.LoadFileItem(e.file.dataItem);

    return;


    if (e.file.dataItem.linkingUrl == "") {
      let payload = {
        ServerRelativeUrl: e.file.dataItem.serverRelativeUrl,
        Filename: e.file.dataItem.name
      }
      this.service.getFileFromSharepoint({ SharepointFileRequestItem: payload }).then((result) => {
        let bytes = this.base64ToArrayBuffer(result.fileResultItem.fileBytes);

        let fName = 'file';
        if (e.file != null && e.file.name != null) fName = e.file.name;
        if (result.fileResultItem.fileName != null) fName = result.fileResultItem.fileName;

        this.saveByteArray(fName, result.fileResultItem.fileType, bytes);

      });
    } else {
      window.open(e.file.dataItem.linkingUrl)
    }
  }

  base64ToArrayBuffer(base64) {
    var binaryString = window.atob(base64);
    var binaryLen = binaryString.length;
    var bytes = new Uint8Array(binaryLen);
    for (var i = 0; i < binaryLen; i++) {
      var ascii = binaryString.charCodeAt(i);
      bytes[i] = ascii;
    }
    return bytes;
  }

  saveByteArray(reportName, fileType, byte) {

    let mimeType = "";

    switch (fileType) {
      case ("png"):
      case ("jpeg"):
        mimeType = `image/${fileType}`;
        break;
      case ("csv"):
      case ("txt"):
        mimeType = `text/${fileType}`;
        break;
      case ("pdf"):
      case ("json"):
      case ("zip"):
        mimeType = `application/${fileType}`;
        break;
      case ("xls"):
        mimeType = `application/vnd.ms-excel`;
        break;
      case ("ppt"):
        mimeType = `application/vnd.ms-powerpoint`;
        break;
      case ("odt"):
        mimeType = `application/vnd.oasis.opendocument.text`;
        break;
      case ("pptx"):
        mimeType = `application/vnd.openxmlformats-officedocument.presentationml.presentation`;
        break;
      case ("xlsx"):
        mimeType = `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
        break;
      case ("docx"):
        mimeType = `application/vnd.openxmlformats-officedocument.wordprocessingml.document`;
        break;
    }

    var blob = new Blob([byte], { type: mimeType });
    let link = window.URL.createObjectURL(blob);

    if (reportName == null) {
      window.open(link).focus();
      return;
    }

    const anchorElement = document.createElement('a');
    document.body.appendChild(anchorElement);
    anchorElement.style.display = 'none';

    anchorElement.href = link;
    anchorElement.download = reportName;
    anchorElement.click();

    window.URL.revokeObjectURL(link);
    anchorElement.remove();

  };

  handleContextMenuShowing(ev: any): void {
    if (ev.fileSystemItem == null) { ev.cancel = true; return; }
    if (ev.fileSystemItem.isDirectory == true) { ev.cancel = true; return; }


  }

  onContextMenuShowing(ev: any) {

    if (ev.fileSystemItem == null) { ev.cancel = true; return; }
    if (ev.fileSystemItem.isDirectory == true) { ev.cancel = true; return; }

    if (this.fileViewer == null) return;

    this.fileViewer.LoadFileItem(ev.fileSystemItem.dataItem);

    return;

    if (ev.fileSystemItem.dataItem.linkingUrl == "") {
      let payload = {
        ServerRelativeUrl: ev.fileSystemItem.dataItem.serverRelativeUrl,
        Filename: ev.fileSystemItem.name
      }
      this.service.getFileFromSharepoint({ SharepointFileRequestItem: payload }).then((result) => {
        let bytes = this.base64ToArrayBuffer(result.fileResultItem.fileBytes);

        let fName = 'file';
        if (ev.fileSystemItem != null && ev.fileSystemItem.name != null) fName = ev.fileSystemItem.name;
        if (result.fileResultItem.fileName != null) fName = result.fileResultItem.fileName;

        this.saveByteArray(fName, result.fileResultItem.fileType, bytes);

      });
    } else {
      window.open(ev.fileSystemItem.dataItem.linkingUrl)
    }
  }

  multiDownloadFiles: any = [];
  multiDownloadInprogress: boolean = false;

  handleDownloading(e: any): void {
    e.cancel = true;
    this.multiDownloadFiles.push(e.item)
    if (this.multiDownloadInprogress === true) return;
    this.multiDownloadInprogress = true;
    this.appGlobals.ShowGlobalLoader();
    setTimeout(() => {
      this.initMultiFileDownload();
    }, 2000);
  }

  initMultiFileDownload(): void {

    this.appGlobals.HideGlobalLoader();
    let payload = {
      ProjectId: this.projectId,
      DownloadRequestFilePaths: []

    };

    this.multiDownloadFiles.forEach((f: any) => {

      let parentPath = '';
      if (f.parentPath == null || f.parentPath == '') parentPath = f.dataItem.serverRelativeUrl;
      else parentPath = f.parentPath;

      payload.DownloadRequestFilePaths.push({
        FileName: f.name,
        FilePath: parentPath
      });
    });

    this.service.getMultipleFilesForDownload(payload).then(
      (result) => {

        this.multiDownloadInprogress = false;
        this.multiDownloadFiles = [];

        if (result.hasError === true) AppGlobals.ShowErrorMessage(result.feedback);
        else {

          if (result.hasMissingFiles === true) {
            AppGlobals.ShowErrorMessage(`The following files could not be downloaded. Please contact your system administrator: ${result.faultyFiles}`);
          }

          if (result.successfulFileCount > 0 && result.downloadId) this.apiFileService.downloadTempFile(result.downloadId, result.downloadFileName);

        }
      },
      (error) => { AppGlobals.ShowErrorMessage(error); });
  }


  handleErrorOccurred(e: any): void {
  }


  handleDocumentSearch(e: any): void {
    //result.rootFolder
    //this.files
    // this.filesBackup;
    // isDirectory

    if (e != null) this.searchTerm = e.event.currentTarget.value;

    if (this.searchTerm == null) this.searchTerm = '';

    if (this.searchTerm == '') {
      this.files = this.filesBackup;

      setTimeout(() => {
        this.docView.instance.refresh();
        this.docView.instance.repaint();
      }, 100)
      return;
    }

    if (this.searchTerm.length < 3) return;
    this.tempSearchFiles = [];
    console.clear();

    if (this.filesBackup.length == 0) return;

    // this.filesLoaded = false;
    this.getSearchResults(this.filesBackup[0]);

    this.files = this.tempSearchFiles;
    this.preselectedFolder = '';



  }

  getSearchResults(dir: any) {

    if (dir.items == null) return;
    if (dir.items.length === 0) return;

    let localSearchTerm = this.searchTerm.toLowerCase();

    for (let i = 0; i < dir.items.length; i++) {

      let item = dir.items[i];
      let compareTerm = item.name.toLowerCase();

      if (compareTerm.indexOf(localSearchTerm) != -1) this.tempSearchFiles.push(item);

      if (item.isDirectory === true) this.getSearchResults(item);

    }

  }



  handleSandboxSearch(e: any): void {
    if (e != null) this.sandboxSearchTerm = e.event?.currentTarget?.value ?? this.sandboxSearchTerm;
    if (this.sandboxSearchTerm == null) this.sandboxSearchTerm = '';

    if (this.sandboxSearchTerm === '') {
      this.sandboxFiles = this.sandboxFilesBackup;
      setTimeout(() => {
        this.sandboxView?.instance?.refresh();
        this.sandboxView?.instance?.repaint();
      }, 100);
      return;
    }

    if (this.sandboxSearchTerm.length < 3) return;

    const results: any[] = [];
    const term = this.sandboxSearchTerm.toLowerCase();
    const search = (items: any[]) => {
      for (const item of items) {
        if ((item.name ?? '').toLowerCase().includes(term)) results.push(item);
        if (item.isDirectory && item.items?.length) search(item.items);
      }
    };
    search(this.sandboxFilesBackup);
    this.sandboxFiles = results;
  }

  fileManager_customizeDetailColumns(columns: any) {
    columns.find((c: any) => c.dataField === "dateModified").format = "dd.MM.yyyy";
    columns.push({
      caption: '',
      width: 46,
      alignment: 'center',
      allowSorting: false,
      cellTemplate: 'externallyModifiedCell',
    });
    return columns.map(column => {
      if (column.dataField === "name") {
        column.caption = "Name";
        column.minWidth = 200;
      }
      if (column.dataField === "dateModified") {
        column.caption = $localize`Date Modified`;
        column.width = 170;
      }
      if (column.dataField === "size") {
        column.caption = $localize`File Size`;
        column.width = 100;
      }
      return column;
    });
  }
}
